import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { type SuspensionReasonView, type SuspensionRealmView, type SuspensionView } from '@vello/api-client';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, EmptyState, Field, Notice, PageHeader, Panel, SegmentedControl, Select, ListTable, TextArea, TextInput, FormRow, SectionTitle, FormGrid, Stack, Text, Drawer, DrawerHeader, DrawerBody, DrawerFooter, KeyValueList } from '@vello/web-theme';
import { DrawerStack } from '../components/drawer-stack';
import { IdRef } from '../components/code-ref';
import {
  SUSPENSION_STATUS_TONE,
  countryOfMarketId,
  formatMarketInstant,
  marketTimeZone,
  reputationAuthority,
  suspensionPeriod,
  zonedLocalToUtc,
} from '../../src/index';
import { PersonName } from '../components/person-name';
import { MarketField } from '../components/market-field';
import { useConsole } from '../console';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

const REASONS: readonly SuspensionReasonView[] = ['conduct_complaint', 'safety_incident', 'fraud_suspected', 'documents_irregular', 'unpaid_debt', 'identity_mismatch', 'other'];

/**
 * **Operações › Suspensões** (W9-REPUTATION, D478, ADR-0096; DevMobility D-034 "Suspensão manual de prestador", D-035 "Consulta de
 * suspensões ativas e histórico") over `GET/POST /v1/ops/{driver,rider}-suspensions` and `…/:id/lift`.
 *
 * - An operator suspends a driver or a rider FROM a start UNTIL an end (both typed in the market's time zone, stored in UTC), or with
 *   no end until someone lifts it. The person stays signed in, sees the period in his app and cannot go online or ask for a ride;
 *   at the end the suspension simply stops holding — nobody has to remember to reactivate.
 * - The list shows what is open (scheduled or in force) or the history (over or lifted), derived at the moment of the read, with
 *   a reinstate action for an open one. The account's own suspension (no sign-in at all) stays in the person's record.
 * - One primary action on the screen ("Suspender"); a lift asks for a confirmation in the console's own dialog. The market comes
 *   from the operator's scope; a refused read is an error, never an empty list. Points and automatic suspension do not exist.
 */
export function SuspensionsScreen(): ReactElement {
  const { runtime, markets, grants } = useAdmin();
  const { t, lang } = useT();
  const { v, explain } = useVocab();
  const { confirm } = useConsole();
  const authority = reputationAuthority(grants);
  const [market, setMarket] = useState('');
  const [realm, setRealm] = useState<SuspensionRealmView>('DRIVER');
  const [filter, setFilter] = useState<'OPEN' | 'HISTORY'>('OPEN');
  const [rows, setRows] = useState<readonly SuspensionView[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [done, setDone] = useState<string | undefined>(undefined);
  const [userId, setUserId] = useState('');
  const [reason, setReason] = useState<SuspensionReasonView>('conduct_complaint');
  const [note, setNote] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [busy, setBusy] = useState(false);
  // The record drawer (the Directory pattern): the suspension stays selected while the drawer closes.
  const [openId, setOpenId] = useState<string | undefined>(undefined);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const mayView = realm === 'DRIVER' ? authority.mayViewDrivers : authority.mayViewRiders;
  const maySuspend = realm === 'DRIVER' ? authority.maySuspendDrivers : authority.maySuspendRiders;
  const tz = marketTimeZone(market === '' ? undefined : market, markets);

  const load = useCallback(async (): Promise<void> => {
    setError(undefined);
    if (market === '' || !mayView) {
      setRows(undefined);
      return;
    }
    const res = await runtime.reputation.suspensions(realm, { country: countryOfMarketId(market), filter, limit: 100 });
    if (res.ok) setRows(res.value.suspensions);
    else {
      setRows(undefined);
      setError(explain(res.error) ?? v('api.error_code', res.error.code));
    }
  }, [runtime, market, realm, filter, mayView, t]);

  useEffect(() => {
    void load();
  }, [load]);
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== '' && mayView });

  const submit = async (): Promise<void> => {
    setError(undefined);
    setDone(undefined);
    const startsAt = start === '' ? undefined : tz !== undefined ? zonedLocalToUtc(start, tz) : undefined;
    const endsAt = end === '' ? undefined : tz !== undefined ? zonedLocalToUtc(end, tz) : undefined;
    if ((start !== '' && startsAt === undefined) || (end !== '' && endsAt === undefined)) {
      setError(t('rep.sus.form.timeInvalid'));
      return;
    }
    setBusy(true);
    const res = await runtime.reputation.suspend(realm, { userId: userId.trim(), reasonCode: reason, ...(note.trim() !== '' ? { note: note.trim() } : {}), ...(startsAt !== undefined ? { startsAt } : {}), ...(endsAt !== undefined ? { endsAt } : {}) });
    setBusy(false);
    if (!res.ok) {
      setError(explain(res.error) ?? v('api.error_code', res.error.code));
      return;
    }
    setDone(t('rep.sus.form.done'));
    setUserId('');
    setNote('');
    setStart('');
    setEnd('');
    await load();
  };

  const canLift = (s: SuspensionView): boolean => maySuspend && (s.status === 'ACTIVE' || s.status === 'SCHEDULED');
  const drawerRow = rows?.find((r) => r.suspensionId === openId);

  const lift = async (s: SuspensionView): Promise<void> => {
    const answer = await confirm({ title: t('rep.sus.lift.title'), body: t('rep.sus.lift.body'), confirmLabel: t('rep.sus.lift'), cancelLabel: t('rep.sus.lift.cancel'), tone: 'primary', icon: 'shieldAlert' });
    if (!answer.confirmed) return;
    setError(undefined);
    setDone(undefined);
    const res = await runtime.reputation.lift(realm, s.suspensionId);
    if (!res.ok) {
      setError(explain(res.error) ?? v('api.error_code', res.error.code));
      return;
    }
    setDone(t('rep.sus.lift.done'));
    setDrawerOpen(false);
    await load();
  };

  return (
    <>
      <PageHeader title={t('rep.sus.title')} subtitle={t('rep.sus.subtitle')} />

      <Panel>
        <Stack gap={4}>
          <FormRow
            columns="minmax(0, 360px)"
            action={
              <>
                <SegmentedControl
                  ariaLabel={t('rep.sus.realm')}
                  value={realm}
                  onChange={(v: SuspensionRealmView) => setRealm(v)}
                  options={[{ id: 'DRIVER', label: t('rep.sus.realm.DRIVER') }, { id: 'RIDER', label: t('rep.sus.realm.RIDER') }]}
                />
                <SegmentedControl
                  ariaLabel={t('rep.sus.filter')}
                  value={filter}
                  onChange={(v: 'OPEN' | 'HISTORY') => setFilter(v)}
                  options={[{ id: 'OPEN', label: t('rep.sus.filter.OPEN') }, { id: 'HISTORY', label: t('rep.sus.filter.HISTORY') }]}
                />
              </>
            }
          >
            <MarketField id="rep-sus-market" value={market} onChange={setMarket} />
          </FormRow>
          {market === '' && <Notice tone="neutral">{t('rep.marketHint')}</Notice>}
          {error !== undefined && <Notice tone="danger">{error}</Notice>}
          {done !== undefined && <Notice tone="success">{done}</Notice>}
        </Stack>
      </Panel>

      {maySuspend && market !== '' && (
        <Panel>
          <Stack gap={4}>
            <SectionTitle>{t('rep.sus.form.title')}</SectionTitle>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <Stack gap={4}>
                <FormGrid>
                <Field label={t('rep.sus.form.user')} htmlFor="rep-sus-user" hint={t('rep.sus.form.user.hint')}>
                  <TextInput id="rep-sus-user" className="tnum" value={userId} autoComplete="off" onChange={(e) => setUserId(e.target.value)} />
                </Field>
                <Field label={t('rep.sus.form.reason')} htmlFor="rep-sus-reason">
                  <Select id="rep-sus-reason" value={reason} onChange={(e) => setReason(e.target.value as SuspensionReasonView)}>
                    {REASONS.map((r) => (
                      <option key={r} value={r}>{v('reputation.suspension_reason', r)}</option>
                    ))}
                  </Select>
                </Field>
                <Field label={t('rep.sus.form.start')} htmlFor="rep-sus-start" hint={t('rep.sus.form.start.hint')}>
                  <TextInput id="rep-sus-start" className="tnum" type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
                </Field>
                <Field label={t('rep.sus.form.end')} htmlFor="rep-sus-end" hint={t('rep.sus.form.end.hint')}>
                  <TextInput id="rep-sus-end" className="tnum" type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
                </Field>
                </FormGrid>
                <Field label={t('rep.sus.form.note')} htmlFor="rep-sus-note" hint={t('rep.sus.form.note.hint')}>
                  <TextArea id="rep-sus-note" value={note} maxLength={500} rows={2} onChange={(e) => setNote(e.target.value)} />
                </Field>
                <div>
                  <Button type="submit" variant="primary" disabled={busy || userId.trim() === ''}>{t('rep.sus.form.submit')}</Button>
                  <Text role="caption" tone="tertiary" block>{t('rep.sus.form.tz', { tz: tz ?? 'UTC' })}</Text>
                </div>
              </Stack>
            </form>
          </Stack>
        </Panel>
      )}

      <section aria-label={t('rep.sus.list')}>
        <ListTable
          columns={[
            {
              key: 'person',
              header: t('rep.sus.col.person'),
              render: (s: SuspensionView) => (s.userName !== undefined ? <span>{s.userName}</span> : <PersonName id={s.userId} />),
            },
            {
              key: 'period',
              header: t('rep.sus.col.period'),
              render: (s: SuspensionView) => {
                const p = suspensionPeriod(s, tz, lang);
                return (
                  <span className="tnum">
                    {p.from}
                    <Text role="caption" tone="tertiary" block>{p.until !== undefined ? t('rep.sus.until', { at: p.until }) : t('rep.sus.indefinite')}</Text>
                  </span>
                );
              },
            },
            {
              key: 'reason',
              header: t('rep.sus.col.reason'),
              render: (s: SuspensionView) => <span>{v('reputation.suspension_reason', s.reasonCode)}</span>,
            },
            {
              key: 'status',
              header: t('rep.sus.col.status'),
              render: (s: SuspensionView) => (s.status !== undefined ? <Badge tone={SUSPENSION_STATUS_TONE[s.status]}>{v('reputation.suspension_status', s.status)}</Badge> : <span>—</span>),
            },
          ]}
          rows={rows ?? []}
          rowKey={(s) => s.suspensionId}
          onRowClick={(s) => {
            setOpenId(s.suspensionId);
            setDrawerOpen(true);
          }}
          selectedKey={drawerOpen ? openId : undefined}
          actions={(s) => (canLift(s) ? <Button size="sm" variant="secondary" onClick={() => void lift(s)}>{t('rep.sus.lift')}</Button> : null)}
          loading={market !== '' && rows === undefined && error === undefined}
          loadingLabel={t('ui.loading')}
          error={error !== undefined ? t('ui.loadFailed') : undefined}
          onRetry={() => void load()}
          retryLabel={t('ui.retry')}
          empty={<EmptyState title={market === '' ? t('rep.marketHint') : t(filter === 'OPEN' ? 'rep.sus.empty.open' : 'rep.sus.empty.history')} />}
        />
      </section>

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} labelledBy="rep-sus-drawer-title">
        {drawerRow !== undefined && (() => {
          const p = suspensionPeriod(drawerRow, tz, lang);
          return (
            <>
              <DrawerHeader
                titleId="rep-sus-drawer-title"
                title={drawerRow.userName ?? <PersonName id={drawerRow.userId} />}
                actions={drawerRow.status !== undefined ? <Badge tone={SUSPENSION_STATUS_TONE[drawerRow.status]}>{v('reputation.suspension_status', drawerRow.status)}</Badge> : undefined}
                onClose={() => setDrawerOpen(false)}
                closeLabel={t('ui.close')}
              />
              <DrawerBody>
                <DrawerStack>
                  <KeyValueList
                    items={[
                      { label: t('rep.sus.realm'), value: t(`rep.sus.realm.${drawerRow.realm}`) },
                      { label: t('rep.sus.drawer.account'), value: <IdRef value={drawerRow.userId} copy full />, mono: true },
                      { label: t('rep.sus.col.period'), value: `${p.from} → ${p.until ?? t('rep.sus.indefinite')}`, mono: true },
                      { label: t('rep.sus.col.reason'), value: v('reputation.suspension_reason', drawerRow.reasonCode) },
                      ...(drawerRow.note !== undefined ? [{ label: t('rep.sus.form.note'), value: drawerRow.note }] : []),
                      { label: t('rep.sus.drawer.createdBy'), value: <PersonName id={drawerRow.createdBy} /> },
                      { label: t('rep.sus.drawer.createdAt'), value: formatMarketInstant(drawerRow.createdAt, tz, lang), mono: true },
                      ...(drawerRow.liftedBy !== undefined ? [{ label: t('rep.sus.drawer.liftedBy'), value: <PersonName id={drawerRow.liftedBy} /> }] : []),
                      ...(drawerRow.liftedAt !== undefined ? [{ label: t('rep.sus.drawer.liftedAt'), value: formatMarketInstant(drawerRow.liftedAt, tz, lang), mono: true }] : []),
                      { label: t('rep.sus.drawer.id'), value: <IdRef value={drawerRow.suspensionId} copy full />, mono: true },
                    ]}
                  />
                </DrawerStack>
              </DrawerBody>
              {canLift(drawerRow) && (
                <DrawerFooter>
                  <Button variant="secondary" block onClick={() => void lift(drawerRow)}>{t('rep.sus.lift')}</Button>
                </DrawerFooter>
              )}
            </>
          );
        })()}
      </Drawer>
    </>
  );
}
